import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { Button, Pills, Loading, PillsState, Alert } from "barte-design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSettings } from "@/hooks";
import { cpf_cnpj, copyTextToClipboard, showToast } from "@/utils";
import { getTenantConfig } from "@/config/tenantConfig";
import { BiometricSession, BiometricSessionStatus } from "@/models";

interface BiometriaStepProps {
  accountId: string;
  onAllApproved?: () => void;
}

const statusLabel: Record<BiometricSessionStatus, string> = {
  CREATED: "Não iniciado",
  IN_ANALYSIS: "Em análise",
  EXPIRED: "Expirado",
  REPROVED: "Reprovado",
  APPROVED: "Aprovado",
  UNKNOWN: "Desconhecido",
};

const statusState: Record<BiometricSessionStatus, PillsState> = {
  CREATED: "info",
  IN_ANALYSIS: "alert",
  EXPIRED: "error",
  REPROVED: "error",
  APPROVED: "success",
  UNKNOWN: "default",
};

const roleLabel: Record<string, string> = {
  SHAREHOLDER: "Sócio",
  LEGAL_REPRESENTATIVE: "Representante legal",
  PROXYHOLDER: "Procurador",
  ATTORNEY: "Procurador",
  PARTNER: "Sócio",
  OTHER: "Outros",
};

const isActionable = (status: BiometricSessionStatus) =>
  status === "REPROVED" || status === "EXPIRED";

const formatRoles = (roles: string[]) =>
  roles.map((role) => roleLabel[role] ?? role).join(", ");

export function BiometriaStep({ accountId, onAllApproved }: BiometriaStepProps) {
  const { getBiometricSessions, renewBiometricSession } = useSettings();
  const [sessions, setSessions] = useState<BiometricSession[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [renewingDocument, setRenewingDocument] = useState<string | null>(null);

  const { logoUrl, applicationName } = getTenantConfig();
  const onAllApprovedRef = useRef(onAllApproved);
  onAllApprovedRef.current = onAllApproved;

  const selectedSession = sessions.find((session) => session.document === selectedDocument) ?? null;
  const allApproved = sessions.length > 0 && sessions.every((s) => s.sessionStatus === "APPROVED");

  const fetchSessions = useCallback(async () => {
    const response = await getBiometricSessions(accountId);
    const data: BiometricSession[] = response?.data ?? [];
    setSessions(data);
    setSelectedDocument((current) => {
      if (current && data.some((s) => s.document === current)) return current;
      const pending = data.find((s) => s.sessionStatus !== "APPROVED");
      return (pending ?? data[0])?.document ?? null;
    });
    return data;
  }, [accountId, getBiometricSessions]);

  useEffect(() => {
    setIsLoading(true);
    fetchSessions()
      .then(() => setHasError(false))
      .catch(() => {
        setHasError(true);
        showToast({ message: "Não foi possível carregar a verificação biométrica." });
      })
      .finally(() => setIsLoading(false));
  }, [fetchSessions]);

  useEffect(() => {
    if (allApproved) onAllApprovedRef.current?.();
  }, [allApproved]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchSessions();
      setHasError(false);
    } catch {
      setHasError(true);
      showToast({ message: "Não foi possível atualizar o status da verificação." });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRenew = async (document: string) => {
    setRenewingDocument(document);
    try {
      const response = await renewBiometricSession(accountId, document);
      const newUrl: string | undefined = response?.data?.biometricUrl;
      if (newUrl) {
        setSessions((current) =>
          current.map((session) =>
            session.document === document ? { ...session, biometricUrl: newUrl } : session
          )
        );
      }
      setSelectedDocument(document);
      showToast({ message: "Sessão de verificação renovada.", type: "success" });
    } catch (error: any) {
      const message =
        error?.response?.status === 409
          ? "Já existe uma verificação em andamento para este sócio."
          : "Não foi possível renovar a verificação. Tente novamente.";
      showToast({ message });
    } finally {
      setRenewingDocument(null);
    }
  };

  return (
    <Flex w="100%" flexDir="column">
      <Flex
        as="header"
        alignItems="center"
        justifyContent="space-between"
        padding="1.5rem 6rem"
        borderBottomWidth="1px"
        borderColor="gray.49"
      >
        <img src={logoUrl} alt={applicationName} style={{ height: "32px" }} />
        <Heading fontSize="1.25rem">Verificação de identidade</Heading>
      </Flex>

      <Flex flexDir="column" padding="2rem 6rem" gap="1.5rem">
        <Text color="gray.600" fontSize="0.95rem">
          Para ativar sua conta PJ, cada sócio/representante precisa concluir a verificação biométrica.
          Selecione um sócio para iniciar e mantenha a câmera liberada.
        </Text>

        <Alert
          variant="info"
          title="Recomendamos fazer a verificação pelo celular"
          description="Copie o link da verificação e abra no seu celular para concluir a biometria com mais facilidade."
          showCloseButton={false}
        />

        {isLoading ? (
          <Flex justifyContent="center" padding="3rem">
            <Loading />
          </Flex>
        ) : hasError ? (
          <Flex flexDir="column" alignItems="center" gap="1rem" padding="3rem">
            <Text color="gray.600">Não foi possível carregar a verificação biométrica.</Text>
            <Button variant="outline" onClick={handleRefresh} loading={isRefreshing}>
              Tentar novamente
            </Button>
          </Flex>
        ) : (
          <>
            <Flex justifyContent="space-between" alignItems="center">
              <Heading fontSize="1rem">Sócios e representantes</Heading>
              <Button variant="outline" onClick={handleRefresh} loading={isRefreshing}>
                Atualizar status
              </Button>
            </Flex>

            <Flex flexDir="column" gap="0.5rem">
              {sessions.length === 0 && (
                <Text color="gray.600">Nenhuma sessão de verificação encontrada.</Text>
              )}
              {sessions.map((session) => {
                const isSelected = session.document === selectedDocument;
                return (
                  <Flex
                    key={session.document}
                    alignItems="center"
                    justifyContent="space-between"
                    padding="0.75rem 1rem"
                    borderWidth="1px"
                    borderColor={isSelected ? "blue.200" : "gray.49"}
                    borderRadius="8px"
                    cursor="pointer"
                    onClick={() => setSelectedDocument(session.document)}
                  >
                    <Flex flexDir="column">
                      <Text fontWeight="600">{cpf_cnpj(session.document)}</Text>
                      <Text fontSize="0.8rem" color="gray.600">
                        {formatRoles(session.role)}
                      </Text>
                    </Flex>
                    <Flex alignItems="center" gap="0.75rem">
                      <Pills
                        label={statusLabel[session.sessionStatus]}
                        variant="light"
                        state={statusState[session.sessionStatus]}
                        size="sm"
                      />
                      {isActionable(session.sessionStatus) && (
                        <Button
                          variant="outline"
                          loading={renewingDocument === session.document}
                          onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            handleRenew(session.document);
                          }}
                        >
                          Renovar
                        </Button>
                      )}
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>

            {allApproved && (
              <Flex padding="1rem" backgroundColor="green.100" borderRadius="8px">
                <Text color="green.400">
                  Todas as verificações foram aprovadas. Estamos finalizando a ativação da sua conta.
                </Text>
              </Flex>
            )}

            {selectedSession && !isActionable(selectedSession.sessionStatus) &&
              selectedSession.sessionStatus !== "APPROVED" && (
                <Box>
                  <Flex justifyContent="space-between" alignItems="center" gap="0.75rem" marginBottom="0.5rem">
                    <Text fontWeight="600">
                      Verificação de {cpf_cnpj(selectedSession.document)}
                    </Text>
                    <Flex gap="0.5rem">
                      <Button
                        variant="outline"
                        onClick={() => copyTextToClipboard(selectedSession.biometricUrl, "Link da verificação copiado!")}
                      >
                        Copiar link
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => window.open(selectedSession.biometricUrl, "_blank", "noopener")}
                      >
                        Abrir em nova aba
                      </Button>
                    </Flex>
                  </Flex>
                </Box>
              )}
          </>
        )}
      </Flex>
    </Flex>
  );
}
